import type { FC, ReactNode } from 'react'

import { useState } from 'react'
import { Heart, Layers } from 'lucide-react'
import { cn } from '@/lib/utils'
import { padNumber } from '@/lib/photos'
import Reveal from '@/components/landing/reveal'
import SectionHeading from '@/components/landing/section-heading'
import { REASONS } from '@/constants/birthday'

const ReasonsSection: FC = (): ReactNode => {
    const [flipped, setFlipped] = useState<boolean[]>(() => REASONS.items.map(() => false))
    const total = REASONS.items.length
    const count = flipped.filter(Boolean).length
    const allRead = count === total

    const flip = (index: number): void => {
        setFlipped((current) => current.map((value, cardIndex) => cardIndex === index ? !value : value))
    }

    return (
        <section id='reasons' aria-labelledby='reasons-title' className='relative overflow-hidden py-24 sm:py-32'>
            <div className='mx-auto max-w-6xl px-5 sm:px-8'>
                <Reveal>
                    <SectionHeading
                        id='reasons-title'
                        eyebrow={REASONS.eyebrow}
                        title={REASONS.title}
                        subtitle={REASONS.subtitle}
                    />
                </Reveal>

                <Reveal className='mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row'>
                    <p aria-live='polite' className='rounded-full border border-primary/15 bg-white px-5 py-2.5 text-sm font-bold shadow-sm'>
                        <span className='text-primary'>{count}</span> / {total} {REASONS.readSuffix}
                    </p>

                    <button
                        type='button'
                        onClick={() => setFlipped(REASONS.items.map(() => true))}
                        disabled={allRead}
                        className='inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-bold text-primary-foreground shadow-lg shadow-primary/25 transition hover:brightness-110 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/40 disabled:cursor-default disabled:opacity-50'
                    >
                        <Layers className='size-4' />
                        {REASONS.flipAll}
                    </button>
                </Reveal>

                <ul className='mt-12 grid grid-cols-2 gap-4 sm:gap-5 md:grid-cols-3 lg:grid-cols-4'>
                    {REASONS.items.map((reason, index) => {
                        const open = flipped[index]

                        return (
                            <li key={reason}>
                                <Reveal delay={(index % 4) * 80}>
                                    <button
                                        type='button'
                                        onClick={() => flip(index)}
                                        aria-pressed={open}
                                        className='group block aspect-[3/4] w-full rounded-3xl perspective-[1200px] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/40 sm:aspect-[4/5]'
                                    >
                                        <span
                                            className={cn(
                                                'relative block size-full transition-transform duration-700 transform-3d motion-reduce:transition-none',
                                                open && 'rotate-y-180'
                                            )}
                                        >
                                            <span
                                                aria-hidden={open}
                                                className='absolute inset-0 flex flex-col items-center justify-center rounded-3xl bg-gradient-to-br from-primary to-rose-400 p-4 text-primary-foreground shadow-lg shadow-primary/20 backface-hidden'
                                            >
                                                <Heart className='size-8 fill-current opacity-90 transition group-hover:scale-110' />

                                                <span className='mt-3 text-xs font-extrabold uppercase tracking-[0.25em] opacity-90'>
                                                    {REASONS.frontLabel}
                                                </span>

                                                <span className='font-heading text-5xl font-semibold'>
                                                    {padNumber(index + 1)}
                                                </span>

                                                <span className='mt-3 rounded-full bg-white/20 px-3 py-1 text-xs font-bold'>
                                                    {REASONS.frontHint}
                                                </span>
                                            </span>

                                            <span
                                                aria-hidden={!open}
                                                className='absolute inset-0 flex flex-col items-center justify-center rounded-3xl border border-primary/15 bg-white p-4 text-center shadow-lg rotate-y-180 backface-hidden sm:p-5'
                                            >
                                                <Heart className='mb-3 size-5 shrink-0 fill-primary text-primary' />

                                                <span className='font-hand text-xl leading-snug text-foreground sm:text-2xl'>
                                                    {reason}
                                                </span>
                                            </span>
                                        </span>
                                    </button>
                                </Reveal>
                            </li>
                        )
                    })}
                </ul>

                {allRead && (
                    <p
                        role='status'
                        className='mx-auto mt-12 max-w-2xl animate-in fade-in slide-in-from-bottom-4 duration-700 text-center font-hand text-3xl leading-snug text-primary text-balance motion-reduce:animate-none sm:text-4xl'
                    >
                        {REASONS.doneMessage}
                    </p>
                )}
            </div>
        </section>
    )
}

export default ReasonsSection
